import { useAppDispatch } from '@hooks/reduxHooks'
import { Button, Checkbox, Dialog, DialogContent, FormControl, FormControlLabel, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { GridToolbarContainer } from '@mui/x-data-grid-pro'
import { StocksTableTabs } from '../StocksTable'
import { productCatalogService } from '@services/ProductCatalogService'
import { storageLocationService } from '@services/storageLocationService'
import { useState } from 'react'
import { ProductType, StorageLocationDto } from '@services/dtos'
import { getStocks, setActiveStocksTab, StockStatesTabs, updateStocksFilters } from '@state/reducers/stockReducer'
import { ProductTypeOptions } from '@utils/constants'
import ServerAutocomplete from '@components/autocomplete/ServerAutocomplete'
import StockAlertsTable from '../StockAlertsTable'
import { UpTransition } from '@components/animations'
import { useSelector } from '@state/store'
import { setProductBatchFilters } from '@state/reducers/productBatchReducer'

const StockStatesTableToolbar = () => {
  const dispatch = useAppDispatch()
  const [selectedStorageLocation, setSelectedStorageLocation] = useState<StorageLocationDto | null>(null)
  const [stockAlertsTableOpen, setStockAlertsTableOpen] = useState(false)
  const { stockFilters, activeTab } = useSelector(({ stock }) => ({
    stockFilters: stock.filters,
    activeTab: stock.activeStocksTab
  }))

  const fetchAllGenericNames = async (search: string) => {
    const page = await productCatalogService.getAllGenericNames(search)
    return page?.datas ?? []
  }
  const fetchStorageLocations = async (search: string) => {
    const page = await storageLocationService.getAll({ search })
    return page?.datas ?? []
  }

  return (<GridToolbarContainer className="m-2 mb-0">
      <div className="flex justify-between items-center w-full flex-wrap">
        <div className="flex items-center space-x-2 flex-wrap">
          <StocksTableTabs />
          <FormControl size="small">
            <InputLabel>Type de produit</InputLabel>
            <Select
              value={stockFilters.productType}
              onChange={(event: SelectChangeEvent) => {
                const value = event.target.value === '' ? undefined : (event.target.value as ProductType)
                dispatch(updateStocksFilters({ productType: value }))
                dispatch(setProductBatchFilters({ productType: value }))
              }}
              className="min-w-[200px] mr-1"
              size="small"
              label="Type de produit"
            >
              <MenuItem value="">
                <em>Tous</em>
              </MenuItem>
              {ProductTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ServerAutocomplete<string>
            autoFetchOnMount
            value={stockFilters.productGenericName}
            fetchService={fetchAllGenericNames}
            onChange={(value: string | null) => {
              dispatch(updateStocksFilters({ productGenericName: value || undefined }))
              dispatch(setProductBatchFilters({ productGenericName: value || undefined }))
            }}
            getValue={(value) => value || null}
            getOptionLabel={(value) => value}
            debounceMs={300}
            placeholder="Rechercher une DCI"
            label="DCI"
            minSearchLength={0}
            width={280}
          />
          {activeTab === StockStatesTabs.ProductBatches && <><ServerAutocomplete<StorageLocationDto>
            autoFetchOnMount
            value={selectedStorageLocation}
            initialOptions={selectedStorageLocation ? [selectedStorageLocation] : []}
            fetchService={fetchStorageLocations}
            onChange={(value: StorageLocationDto | null) => {
              setSelectedStorageLocation(value)
              dispatch(setProductBatchFilters({ storageLocationId: value ? value.id : undefined }))
            }}
            getValue={(value) => value?.id?.toString() || null}
            getOptionLabel={(value) => {
              const storageLocation = value as StorageLocationDto
              return storageLocation ? storageLocation.name : ''
            }}
            debounceMs={300}
            placeholder="Rechercher un stockage"
            label="Stockage"
            minSearchLength={0}
            width={280}
          /><TextField
            label="Recherche"
            onChange={(event) => {
              dispatch(setProductBatchFilters({ search: event.target.value }))
            }}
            size="small"
            placeholder='Rechercher N° lot ou nom du produit'
            sx={{ width: 300 }}
          /></>}
          {activeTab === StockStatesTabs.DCI && <FormControlLabel
            control={
              <Checkbox 
                checked={stockFilters.alertStock} 
                onChange={(event) => dispatch(updateStocksFilters({ alertStock: event.target.checked }))}
              />
            } 
            label="Alerte stock uniquement" />}
        </div>
        {activeTab === StockStatesTabs.DCI && <div>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setStockAlertsTableOpen(true)}
          >
            <i className="fas fa-cog"></i>{' '}
            <span className="ml-2">{'Seuils d\'alerte'}</span>
          </Button>
        </div>}
      </div>

      <div className='w-full'>
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          color="primary"
          onChange={(_, value) => {
            value !== null && dispatch(setActiveStocksTab(value))
          }}
          size="small"
          unselectable="off"
          className='m-3'
          sx={{
              '& .MuiToggleButton-root': {
                '&.Mui-selected': {
                  backgroundColor: '#3A0CA1',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#3A0CA1',
                  },
                },
              },
            }}
          >
          {[{ value: StockStatesTabs.DCI, label: 'DCI' }, { value: StockStatesTabs.ProductBatches, label: 'Lot produits' }]
            .map(({ value, label }) => (
            <ToggleButton key={label} value={value}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>

      {stockAlertsTableOpen && (
        <Dialog
          open={stockAlertsTableOpen}
          onClose={() => {
            setStockAlertsTableOpen(false)
            dispatch(getStocks())
          }}
          TransitionComponent={UpTransition}
          slotProps={{ backdrop: {} }}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent>
            <>
              <div className="flex justify-between items-center mb-2">
                <Typography variant="h6">{'Configuration seuils d\'alerte'}</Typography>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    setStockAlertsTableOpen(false)
                    dispatch(getStocks())
                  }}
                >
                  Fermer
                </Button>
              </div>
              <StockAlertsTable />
            </>
          </DialogContent>
        </Dialog>
      )}
    </GridToolbarContainer>)
}

export default StockStatesTableToolbar
